// Personalización de formularios del odontograma (antecedentes / nueva consulta) por
// consultorio. Persiste en Professional.formsConfig (Json?) — ver prisma/schema.prisma.
// Las preguntas default viven en código (medicalQuestionnaire.ts / OdontogramEditor.tsx);
// esta config solo guarda agregados custom, defaults ocultos y el orden final.

export type CustomQuestionType = "yesno" | "text";

export type CustomQuestion = {
  id: string;
  label: string;
  type: CustomQuestionType;
};

export type FormSectionConfig = {
  customItems?: CustomQuestion[];
  hiddenDefaultIds?: string[];
  order?: string[];
};

export type FormsConfig = {
  antecedentes?: FormSectionConfig;
  nuevaConsulta?: FormSectionConfig;
};

/** Campos fijos de "Nueva consulta": no se pueden ocultar ni eliminar, solo se listan acá
 *  para que la UI de configuración los muestre junto a los custom. */
export const NUEVA_CONSULTA_DEFAULT_FIELDS: { id: string; label: string }[] = [
  { id: "chiefComplaint", label: "Motivo de consulta" },
  { id: "sessionNotes", label: "Notas de la sesión" },
  { id: "treatmentsIndicated", label: "Tratamientos indicados" },
  { id: "treatmentsDone", label: "Tratamientos realizados" },
];

/** Reordena `items` según `order` (comparando por el id que devuelve `getId`); lo que no
 *  esté en `order` (agregados nuevos, defaults recién mostrados) va al final, en su orden original. */
export function applyOrder<T>(items: T[], getId: (item: T) => string, order?: string[]): T[] {
  if (!order || order.length === 0) return items;
  const byId = new Map(items.map((it) => [getId(it), it]));
  const ordered: T[] = [];
  for (const id of order) {
    const it = byId.get(id);
    if (it) {
      ordered.push(it);
      byId.delete(id);
    }
  }
  ordered.push(...byId.values());
  return ordered;
}

/** Lista efectiva de preguntas custom de una sección (ej. "Nueva consulta"), ya ordenada. */
export function resolveCustomFormFields(config?: FormSectionConfig): CustomQuestion[] {
  return applyOrder(config?.customItems ?? [], (c) => c.id, config?.order);
}

export function createCustomQuestion(label: string, type: CustomQuestionType, prefix = "custom"): CustomQuestion {
  const id = `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  return { id, label, type };
}
